import bbox from '@turf/bbox';
import booleanWithin from '@turf/boolean-within';
import { lineString, multiLineString, polygon } from '@turf/helpers';
import { useEffect, useMemo } from 'react';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { COLOR_MAP_POI, COLOR_MAP_POI_OUTLINE, COLOR_MAP_ROUTE, COLOR_MAP_ROUTE_OUTLINE, SPACE_300 } from '@alltrails/denali/tokens';
import getMapBoundingBox from '@alltrails/explore-service/utils/getMapBoundingBox';
import getPolygonForBoundingBox from '@alltrails/explore-service/utils/getPolygonForBoundingBox';
import BaseMap from '@alltrails/maps/components/BaseMap/BaseMap';
import CompassButton from '@alltrails/maps/components/CompassButton/CompassButton';
import MapLayout from '@alltrails/maps/components/MapLayout/MapLayout';
import MapProvider from '@alltrails/maps/components/MapProvider/MapProvider';
import PolylineSource from '@alltrails/maps/components/Polyline/PolylineSource';
import ZoomButtons from '@alltrails/maps/components/ZoomButtons/ZoomButtons';
import useMap from '@alltrails/maps/hooks/useMap';
import { AllTrailsBounds, LngLat } from '@alltrails/maps/types/Geo';
import { decodeLatLngs, reverseCoord } from '@alltrails/maps/utils/legacyGeoJSONConversions';
import { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import styles from './styles.module.scss';

type TrailSegmentsMapProps = {
  activeId?: number;
  trails: TrailSearchResult[];
};

const TrailSegmentsMap = ({ activeId, trails }: TrailSegmentsMapProps) => {
  const map = useMap();
  const isMobile = useIsMobileSizedScreen();

  const polylineData = useMemo(() => {
    return trails
      .filter(trail => trail.polyline)
      .map(trail => {
        const coordinates = decodeLatLngs(trail.polyline!).map(latLng => reverseCoord(latLng));
        if (coordinates && coordinates.length >= 2) {
          const boundingPolygon = polygon([[...coordinates, coordinates[0]]]); // Close the polygon by adding the first point again
          return {
            boundingPolygon,
            coordinates,
            id: trail.ID
          };
        }
        return undefined;
      })
      .filter(Boolean) as { id: number; coordinates: LngLat[]; boundingPolygon: ReturnType<typeof polygon<any>> }[];
  }, [trails]);

  const initialBounds = useMemo<AllTrailsBounds | undefined>(() => {
    const [minLng, minLat, maxLng, maxLat] = bbox(multiLineString(polylineData.map(p => p.coordinates)));
    return {
      longitudeTopLeft: minLng,
      latitudeTopLeft: maxLat,
      longitudeBottomRight: maxLng,
      latitudeBottomRight: minLat,
      padding: SPACE_300
    };
  }, [polylineData]);

  useEffect(() => {
    // If the active trail is not completely contained in the visible map, move the map to the initial bounds
    if (map && initialBounds && activeId) {
      const data = polylineData.find(p => p.id === activeId);

      if (!data) return;

      const { boundingPolygon, coordinates } = data;

      if (!boundingPolygon || !coordinates) return;

      if (!booleanWithin(boundingPolygon, getPolygonForBoundingBox(getMapBoundingBox(map)))) {
        const { latitudeBottomRight, longitudeBottomRight, latitudeTopLeft, longitudeTopLeft } = initialBounds;
        map.fitBounds([
          [longitudeTopLeft, latitudeBottomRight],
          [longitudeBottomRight, latitudeTopLeft]
        ]);
      }
    }
    // Only want to run when the activeId changes
  }, [activeId]);

  return (
    <div className={styles.mapContainer}>
      <BaseMap cooperativeGestures initialBounds={initialBounds} preventReuseMaps>
        {polylineData.map(({ id, coordinates }) => {
          const isInactive = Boolean(activeId && activeId !== id);
          return (
            <PolylineSource
              key={id}
              // Make the active trail polyline the top-most layer so it renders above the other trail segments
              beforeUniqueId={isInactive ? String(activeId) : undefined}
              data={lineString(coordinates)}
              hideArrows={isInactive}
              lineColor={isInactive ? COLOR_MAP_POI : COLOR_MAP_ROUTE}
              lineOutlineColor={isInactive ? COLOR_MAP_POI_OUTLINE : COLOR_MAP_ROUTE_OUTLINE}
              uniqueId={String(id)}
            />
          );
        })}
      </BaseMap>
      {isMobile ? null : (
        <MapLayout
          mapControls={
            <>
              <ZoomButtons />
              <CompassButton />
            </>
          }
        />
      )}
    </div>
  );
};

const TrailSegmentsMapWrapper = (props: TrailSegmentsMapProps) => {
  return (
    <MapProvider>
      <TrailSegmentsMap {...props} />
    </MapProvider>
  );
};

export default TrailSegmentsMapWrapper;
